import React from 'react';
import translations from '../utils/translations';

const ICDisplay = ({ 
  horizontalBins, 
  verticalBins, 
  pinLabels, 
  icName, 
  handlePinLabelChange, 
  handlePinCheckboxChange,
  current_locale = 'en',
  isRTL = false
}) => {
  // Get translations based on locale
  const t = translations[current_locale] || translations['en'];
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'visible',
      position: 'relative',
      padding: '20px',
      background: 'radial-gradient(circle, rgba(240,240,240,0.5) 0%, rgba(255,255,255,0) 70%)',
      borderRadius: '50%'
    }}>
      {/* IC Chip with pins */}
      <div 
        style={{
          display: 'grid',
          position: 'relative',
          gap: '2px',
          margin: '0 auto',
          width: '100%',
          height: 'auto',
          maxWidth: '600px',
          gridAutoFlow: 'dense',
          gridTemplateColumns: `55px repeat(${Math.max(horizontalBins, 1)}, 1fr) 55px`,
          gridTemplateRows: `40px repeat(${Math.max(verticalBins, 1)}, 1fr) 40px`,
          width: `${Math.max(horizontalBins * 60, 300)}px`,
          height: `${Math.max(verticalBins * 60, 300)}px`,
          boxSizing: 'border-box',
          direction: 'ltr' /* Always keep pins in LTR mode */
        }}
      >
        {/* Top pins */}
        {horizontalBins > 0 && Array.from({ length: horizontalBins }).map((_, index) => (
          <div 
            key={`top-${index}`} 
            style={{ 
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gridColumn: index + 2,
              gridRow: 1,
              flexDirection: 'column',
              height: '40px'
            }}
          >
            <input
              type="checkbox"
              checked={!!pinLabels[`top-${index}`]?.checked}
              onChange={e => handlePinCheckboxChange(`top-${index}`, e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#3498db',
                marginBottom: '2px',
                marginTop: '2px',
                borderRadius: '3px',
                border: '1px solid #999',
                background: 'white',
                boxSizing: 'border-box',
              }}
            />
            <input
              type="text"
              style={{
                fontSize: '12px',
                width: '50px',
                border: 'none',
                background: 'transparent',
                textAlign: 'center',
                margin: '5px 0'
              }}
              value={pinLabels[`top-${index}`]?.label || ''}
              onChange={e => handlePinLabelChange(`top-${index}`, e.target.value)}
            />
          </div>
        ))}

        {/* Right pins */}
        {verticalBins > 0 && Array.from({ length: verticalBins }).map((_, index) => (
          <div 
            key={`right-${index}`} 
            style={{ 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gridColumn: Math.max(horizontalBins, 1) + 2,
              gridRow: index + 2,
              flexDirection: 'row',
              justifyContent: 'flex-start'
            }}
          >
            <input
              type="text"
              style={{
                fontSize: '12px',
                width: '50px',
                border: 'none',
                background: 'transparent',
                textAlign: 'right',
                margin: '0 5px'
              }}
              value={pinLabels[`right-${index}`]?.label || ''}
              onChange={e => handlePinLabelChange(`right-${index}`, e.target.value)}
            />
            <input
              type="checkbox"
              checked={!!pinLabels[`right-${index}`]?.checked}
              onChange={e => handlePinCheckboxChange(`right-${index}`, e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#3498db',
                margin: '0 3px',
                borderRadius: '3px',
                border: '1px solid #999',
                background: 'white',
                boxSizing: 'border-box',
                minWidth: '16px',
                minHeight: '16px',
                display: 'block',
              }}
            />
          </div>
        ))}

        {/* Bottom pins */}
        {horizontalBins > 0 && Array.from({ length: horizontalBins }).map((_, index) => (
          <div 
            key={`bottom-${index}`} 
            style={{ 
              position: 'relative',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gridColumn: Math.max(horizontalBins, 1) + 1 - index,
              gridRow: Math.max(verticalBins, 1) + 2,
              flexDirection: 'column',
              height: '40px'
            }}
          >
            <input
              type="checkbox"
              checked={!!pinLabels[`bottom-${index}`]?.checked}
              onChange={e => handlePinCheckboxChange(`bottom-${index}`, e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#3498db',
                marginBottom: '2px',
                marginTop: '2px',
                borderRadius: '3px',
                border: '1px solid #999',
                background: 'white',
                boxSizing: 'border-box',
              }}
            />
            <input
              type="text"
              style={{
                fontSize: '12px',
                width: '50px',
                border: 'none',
                background: 'transparent',
                textAlign: 'center',
                margin: '5px 0'
              }}
              value={pinLabels[`bottom-${index}`]?.label || ''}
              onChange={e => handlePinLabelChange(`bottom-${index}`, e.target.value)}
            />
          </div>
        ))}

        {/* Left pins */}
        {verticalBins > 0 && Array.from({ length: verticalBins }).map((_, index) => (
          <div 
            key={`left-${index}`} 
            style={{ 
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              gridColumn: 1,
              gridRow: verticalBins + 1 - index,
              flexDirection: 'row',
              marginLeft: '-15px' // Move pins 15px to the left
            }}
          >
            <input
              type="checkbox"
              checked={!!pinLabels[`left-${index}`]?.checked}
              onChange={e => handlePinCheckboxChange(`left-${index}`, e.target.checked)}
              style={{
                width: '16px',
                height: '16px',
                accentColor: '#3498db',
                margin: '0 3px',
                borderRadius: '3px',
                border: '1px solid #999',
                background: 'white',
                boxSizing: 'border-box',
                minWidth: '16px',
                minHeight: '16px',
                display: 'block',
              }}
            />
            <input
              type="text"
              style={{
                fontSize: '12px',
                width: '65px', // Increased width for pin labels
                border: 'none',
                background: 'transparent',
                textAlign: 'left',
                margin: '0 5px'
              }}
              value={pinLabels[`left-${index}`]?.label || ''}
              onChange={e => handlePinLabelChange(`left-${index}`, e.target.value)}
            />
          </div>
        ))}

        {/* IC body */}
        <div 
          style={{
            gridColumn: `2 / span ${Math.max(horizontalBins, 1)}`,
            gridRow: `2 / span ${Math.max(verticalBins, 1)}`,
            background: 'linear-gradient(145deg, #2c3e50, #34495e)',
            borderRadius: '10px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: 'white',
            fontSize: '24px',
            fontWeight: 'bold',
            boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.1)',
            position: 'relative',
            border: '1px solid #1a2530',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.5)'
          }}
        >
          {icName}
          {/* Notch indicator */}
          <div 
            style={{
              position: 'absolute',
              top: '10px',
              left: '10px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, #777 0%, #555 60%, #444 100%)',
              border: '2px solid #666',
              boxShadow: 'inset 0 1px 3px rgba(0, 0, 0, 0.4)'
            }}
          />
          {/* Subtle pattern overlay */}
          <div 
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.05,
              backgroundImage: 'linear-gradient(45deg, #000 25%, transparent 25%, transparent 75%, #000 75%, #000), linear-gradient(45deg, #000 25%, transparent 25%, transparent 75%, #000 75%, #000)',
              backgroundSize: '4px 4px',
              backgroundPosition: '0 0, 2px 2px',
              pointerEvents: 'none',
              borderRadius: '10px'
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default ICDisplay;
